import React, { useState, useEffect, useRef } from "react";
import "../../../../../css/RecruitmentScreen/Sections.css";
import { writeDataS9 } from "../../../../../backend/Sections/Section9/writeDataS9";
import { readDataS9 } from "../../../../../backend/Sections/Section9/readDataS9";
import CheckboxGrid from "../../../../DesignSystem/Inputs/CheckboxGrid";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";
import { shouldBypassValidation } from "../../Utils/validationPolicy";
import { validateSection9 } from "../Validation/section9";
import TextArea from "../../../../DesignSystem/Inputs/TextArea";
import FileUpload from "../../../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../../../DesignSystem/Upload/UploadedFileList";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";

const Section9 = ({ onValidationStatusChange }) => {
  const [formData, setFormData] = useState({
    additionalInformation: "",
    files: {}, // { [documentKey]: File[] OR {name,url}[] }
    generalFiles: [],

    checkAndDocumentEnclosed: [],

    financialIssue: [],
    CCJs: [],
    debtManagementPlan: [],
    defaults: [],
    IVTrustDeed: [],
    complaints: [],
    warnings: [],
    suspension: [],
    convictions: [],
    directorships: [],
    liquidations: [],

    brandingAndMarketingSupportOptions: [],
    brandingAndMarketingServices: [],
  });

  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const sectionTopRef = useRef(null);

  // ---- submission listener ----
  useEffect(() => {
    const removeListener = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => removeListener();
  }, []);

  // ---- Re-run validation whenever relevant fields change ----
  useEffect(() => {
    validateSection9({
      formData,
      setErrors,
      setIsValid,
      onValidationStatusChange,
    });
  }, [
    formData.brandingAndMarketingSupportOptions,
    formData.brandingAndMarketingServices,
    formData.checkAndDocumentEnclosed,
    formData.files,
  ]);

  // ---- checkbox change handler ----
  const handleCheckboxChange = (e, formDataKey, option) => {
    const { checked } = e.target;

    setFormData((prev) => {
      const selected = prev[formDataKey] || [];
      const next = checked
        ? [...selected, option]
        : selected.filter((x) => x !== option);

      // If a document is unticked, remove its uploaded files (keeps it clean)
      if (formDataKey === "checkAndDocumentEnclosed" && !checked) {
        const nextFiles = { ...(prev.files || {}) };
        delete nextFiles[option];
        return { ...prev, [formDataKey]: next, files: nextFiles };
      }

      return { ...prev, [formDataKey]: next };
    });
  };

  // ---- file upload per document ----
  const handleFileChange = (e, documentKey) => {
    let newFiles = [];

    // Handle both drag-drop and file input change events
    if (e.dataTransfer) {
      newFiles = Array.from(e.dataTransfer.files || []);
    } else if (e.target.files) {
      newFiles = Array.from(e.target.files || []);
    }

    // console.log("handleFileChange called:", {
    //   documentKey,
    //   newFiles: newFiles.map((f) => f.name),
    //   currentFiles: formData.files?.[documentKey] || [],
    // });

    setFormData((prev) => {
      const updatedFiles = {
        ...prev,
        files: {
          ...(prev.files || {}),
          [documentKey]: [
            ...((prev.files || {})[documentKey] || []),
            ...newFiles,
          ],
        },
      };
      // console.log("Updated formData.files:", updatedFiles.files);
      return updatedFiles;
    });
    setFileInputKey(Date.now());
  };

  const handleRemoveFile = (documentKey, fileIndex) => {
    setFormData((prev) => {
      const existing = (prev.files?.[documentKey] || []).filter(
        (_, i) => i !== fileIndex,
      );

      const nextFiles = { ...(prev.files || {}) };
      if (existing.length === 0) delete nextFiles[documentKey];
      else nextFiles[documentKey] = existing;

      return { ...prev, files: nextFiles };
    });

    setFileInputKey(Date.now());
  };

  // ---- general files ----
  const handleGeneralFileChange = (e) => {
    let newFiles = [];

    // Handle both drag-drop and file input change events
    if (e.dataTransfer) {
      newFiles = Array.from(e.dataTransfer.files || []);
    } else if (e.target.files) {
      newFiles = Array.from(e.target.files || []);
    }

    setFormData((prev) => ({
      ...prev,
      generalFiles: [...(prev.generalFiles || []), ...newFiles],
    }));
  };

  const handleRemoveGeneralFile = (fileIndex) => {
    setFormData((prev) => ({
      ...prev,
      generalFiles: (prev.generalFiles || []).filter((_, i) => i !== fileIndex),
    }));
  };

  // ---- fetch existing data on mount ----
  useEffect(() => {
    const fetchData = async () => {
      const data = await readDataS9();
      if (data) {
        const merged = { ...formData, ...data };
        setFormData(merged);

        validateSection9({
          formData: merged,
          setErrors,
          setIsValid,
          onValidationStatusChange,
        });
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowErrors(true);
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });

    // console.log("Section 9 - Submit clicked");
    // console.log("Section 9 - Form Data:", formData);
    // console.log("Section 9 - showErrors set to:", true);

    const ok = validateSection9({
      formData,
      setErrors,
      setIsValid,
      onValidationStatusChange,
    });

    // console.log("Section 9 - Validation Result:", ok);
    // console.log("Section 9 - Errors after validation:", errors);

    // Save data regardless of validation status
    await writeDataS9(formData);

    // Show appropriate alert based on validation result
    if (ok || shouldBypassValidation()) {
      setAlertMessage("Data saved successfully!");
      setAlertType("success");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  // ---------- options arrays ----------
  const checkAndDocumentEnclosed = [
    "Passport",
    "Driving Licence",
    "Proof of Address - Dated within the last 3 months",
    "Certificates – Copies of all industry qualifications / Higher level qualifications",
    "SPS – Required only for Diploma qualified Financial Planners. Financial Planners who are new to the industry will not have a current SPS and will need to apply once FCA authorisation has been granted, and prior to appointment",
    "Data Protection Licence (DPL) – In date and in the correct name – https://ico.org.uk/registration/new/about",
    "Credit Report - Dated within the last 3 months",
     ];

  const financialIssue = [
    "Not applicable",
    "Certificate of discharge",
    "Signed statement detailing events and reasons for occurrence",
    "Formal list of Creditors Included with the bankruptcy and amounts",
  ];

  const CCJs = [
    "Not applicable",
    "Evidence of up to date repayment plan OR Certificate of Satisfaction - please note any arrangements will need to have been in force for the last 6 months",
    "Signed statement detailing events, creditors, amounts and reasons for CCJs occurring",
  ];

  const debtManagementPlan = [
    "Not applicable",
    "Evidence of repayment plan and that this is up to date - please note this arrangement will need to have been in force for at least 6 months",
    "Signed statement detailing events, creditors, amounts and reasons for debt occurring",
    "A copy of the original documentation confirming when the original debt plan/arrangement commenced detailing creditors and amounts",
  ];

  const defaults = [
    "Not applicable",
    "Signed statement detailing events, creditors, amounts and reasons for defaults/mortgage arrears occurring",
    "Evidence from each lender that a repayment plan has been put in place which is paid to date or amounts have been cleared - please note arrangements will need to have been in place for at least 6 months",
  ];

  const IVTrustDeed = [
    "Not applicable",
    "Signed statement detailing events, creditors, amounts and reasons for IVA/Trust Deed occurring.",
    "A statement from the insolvency practitioner should be included, or a copy of their report",
    "Formal list of creditors included with IVA/Trust Deed and amounts - please note these arrangements will need to have been in place for at least 6 months",
  ];

  const complaints = [
    "Not applicable",
    "Any documentation held",
    "Details of complaints, dates and where authorised when it/they occurred",
    "Client names and outcomes / redress amounts",
    "Disciplinary",
  ];

  const warnings = [
    "Not applicable",
    "Any documentation held",
    "Details of Warning / Breach / Investigation, date and where authorised when it occurred",
    "Outcome, including any additional training / remedial action",
  ];

  const suspension = [
    "Not applicable",
    "Any documentation held",
    "Details of Suspension/Termination, date and, where authorised, when it occurred",
    "Outcome, including any additional training / remedial action",
  ];

  const convictions = [
    "Not applicable",
    "Any documentation held",
    "Details of sentence or penalty",
    "Client names and outcomes / redress amounts",
    "Disciplinary",
  ];

  const directorships = [
    "Not applicable",
    "All directorships should be disclosed in application to include future intentions, any liabilities and how this will fit in with your Rosemount contract or will be kept separate and distinct",
  ];

  const liquidations = [
    "Not applicable",
    "Liquidator’s report",
    "Details of company name and number, date and how / why it occurred",
    "Details of any enforcements as a result",
  ];

  const brandingAndMarketingSupportOptions = [
    "I already have branding & don’t need support",
    "I have branding but need stationery & marketing materials",
    "I need help creating/updating my branding",
    "I’m not sure - I’d like to speak to someone for guidance",
  ];

  const brandingAndMarketingServices = [
    "Full brand creation (e.g. logo, colour palette, fonts)",
    "Brand refresh",
    "Website design or rebuild",
    // "Assisting with running social media accounts",
    "Business stationery (e.g. business cards, letterheads, email signatures)",
    "Social media branding (e.g. images, banners, templates)",
    "Marketing materials (e.g. brochures, flyers, presentations)",
    "I don’t need any branding or marketing support",
  ];

  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertMessage("")}
        />
      )}
      <h3>Section 9 – Additional Information</h3>
      <div className="Content">
        Please use this space to provide further details on information
        previously provided in this application
      </div>

      <TextArea
        className="long-textarea"
        readOnly={isSubmitted}
        placeholder="Enter your text here..."
        name="additionalInformation"
        value={formData.additionalInformation}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            additionalInformation: e.target.value,
          }))
        }
      />

      <div className="separator" />
      <div className="Content">9.2 Branding and Marketing Support</div>

      <div className="input-group-row">
        <div className="Content">
          1. Do you require branding or marketing support?
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <CheckboxGrid
            options={brandingAndMarketingSupportOptions}
            columns={1}
            error={errors.brandingAndMarketingSupportOptions}
            formDataKey="brandingAndMarketingSupportOptions"
            selectedOptions={formData.brandingAndMarketingSupportOptions}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>
      <div className="input-group-row">
        <div className="Content">
          2. Which of the following branding or marketing services do you
          require? (Select all that apply)
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <CheckboxGrid
            options={brandingAndMarketingServices}
            columns={1}
            error={errors.brandingAndMarketingServices}
            formDataKey="brandingAndMarketingServices"
            selectedOptions={formData.brandingAndMarketingServices}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="separator" />
      <div className="Content">9.3 Recruitment Checklist</div>
      <div className="Content-Small">
        So that we can process your application accurately and as quickly as
        possible, please use this checklist and ensure you submit the necessary
        documents and information with your application.
      </div>

      <div className="Content">Checks & Documents to Enclose</div>

      {checkAndDocumentEnclosed.map((documentKey, index) => (
        <div key={index} className="form-row">
          <CheckboxGrid
            options={[documentKey]}
            columns={1}
            formDataKey="checkAndDocumentEnclosed"
            selectedOptions={formData.checkAndDocumentEnclosed || []}
            handleCheckboxChange={handleCheckboxChange}
          />

          {formData.checkAndDocumentEnclosed.includes(documentKey) && (
            <>
              {/* {console.log(`FileUpload for ${documentKey}:`, {
                showErrors,
                hasError: !!errors[documentKey],
                errorMessage: errors[documentKey],
                computedStatus:
                  showErrors && errors[documentKey] ? "error" : "default",
                computedMessage:
                  showErrors && errors[documentKey] ? errors[documentKey] : "",
              })} */}
              <FileUpload
                description="Drag & drop files here or"
                browseText="Browse"
                status={showErrors && errors[documentKey] ? "error" : "default"}
                statusMessage={
                  showErrors && errors[documentKey] ? errors[documentKey] : ""
                }
                onDrop={(e) => handleFileChange(e, documentKey)}
                onBrowse={() =>
                  document.getElementById(`fileInput-${documentKey}`).click()
                }
              />
              <input
                type="file"
                multiple
                onChange={(e) => handleFileChange(e, documentKey)}
                style={{ display: "none" }}
                id={`fileInput-${documentKey}`}
              />

              {Array.isArray(formData.files?.[documentKey]) &&
                formData.files[documentKey].length > 0 && (
                  <UploadedFileList
                    files={formData.files[documentKey]}
                    onRemove={(file, index) =>
                      handleRemoveFile(documentKey, index)
                    }
                  />
                )}

              {/* <div className="sizebox" /> */}
            </>
          )}
        </div>
      ))}

      <div className="separator" />
      <div className="Content">
        9.4 Additional Disclosure & Paperwork Required
      </div>

      <div className="Content">Financial Issues</div>
      <div className="input-group-row">
        <div className="Content">
          1. Bankruptcy – Bankruptcy has to be discharged
        </div>
        <div className="input-group">
          <CheckboxGrid
            options={financialIssue}
            columns={1}
            formDataKey="financialIssue"
            selectedOptions={formData.financialIssue}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>
      <div className="input-group-row">
        <div className="Content">
          2. CCJs – Must be settled or in a repayment plan
        </div>
        <div className="input-group">
          <CheckboxGrid
            options={CCJs}
            columns={1}
            formDataKey="CCJs"
            selectedOptions={formData.CCJs}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          3. Debt Management Plan / Arrangements - Whether through a company or
          arrangements made directly with creditor(s).
        </div>
        <div className="input-group">
          <CheckboxGrid
            options={debtManagementPlan}
            columns={1}
            formDataKey="debtManagementPlan"
            selectedOptions={formData.debtManagementPlan}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">4. Defaults / Mortgage Arrears</div>
        <div className="input-group">
          <CheckboxGrid
            options={defaults}
            columns={1}
            formDataKey="defaults"
            selectedOptions={formData.defaults}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          5. IVA/Trust Deed{" "}
          <div className="Content-Small">
            Individual Voluntary Arrangement (UK) or Trust Deed (Scotland) –
            Application may proceed on a case-by-case basis if still in force.
          </div>
        </div>
        <div className="input-group">
          <CheckboxGrid
            options={IVTrustDeed}
            columns={1}
            formDataKey="IVTrustDeed"
            selectedOptions={formData.IVTrustDeed}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="separator" />
      <div className="Content">Complaints</div>

      <div className="input-group-row">
        <div className="Content">1. Complaints</div>
        <div className="input-group">
          <CheckboxGrid
            options={complaints}
            columns={1}
            formDataKey="complaints"
            selectedOptions={formData.complaints}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">2. Warnings / Breaches / Investigations</div>
        <div className="input-group">
          <CheckboxGrid
            options={warnings}
            columns={1}
            formDataKey="warnings"
            selectedOptions={formData.warnings}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">3. Suspension / Termination</div>
        <div className="input-group">
          <CheckboxGrid
            options={suspension}
            columns={1}
            formDataKey="suspension"
            selectedOptions={formData.suspension}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="separator" />
      <div className="Content">Additional information</div>

      <div className="input-group-row">
        <div className="Content">
          1. Convictions
          <div className="Content-Small">
            (Excluding motoring unless resulted in a ban) – All convictions
            regardless of whether they are now spent
          </div>
        </div>
        <div className="input-group">
          <CheckboxGrid
            options={convictions}
            columns={1}
            formDataKey="convictions"
            selectedOptions={formData.convictions}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          2. Directorships{" "}
          <div className="Content-Small">
            This question specifies ‘Have you ever…’
          </div>
        </div>
        <div className="input-group">
          <CheckboxGrid
            options={directorships}
            columns={1}
            formDataKey="directorships"
            selectedOptions={formData.directorships}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>
      <div className="input-group-row">
        <div className="Content">
          3. Liquidations / Company in Administration
        </div>
        <div className="input-group">
          <CheckboxGrid
            options={liquidations}
            columns={1}
            formDataKey="liquidations"
            selectedOptions={formData.liquidations}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      </div>

      <div className="separator" />
      {/* <div className="Content">Regulatory Experience</div>
      <div className="Content-Small">
        If you have regulatory experience, please can you provide copies of your
        Persistency/Cancellation levels, data concerning your suitability of
        advice checks and a copy of your business plan. If applicable copy
        correspondence relating to any complaints either upheld or not upheld,
        copies of any 1:1 reviews, compliance visits or annual inspections.
      </div>

      <FileUpload
        description="Drag & drop files here or"
        browseText="Browse"
        status={showErrors && errors.generalFiles ? "error" : "default"}
        statusMessage={
          showErrors && errors.generalFiles ? errors.generalFiles : ""
        }
        onDrop={(e) => handleGeneralFileChange(e)}
        onBrowse={() => document.getElementById("fileInput").click()}
      />
      <input
        type="file"
        multiple
        onChange={(e) => handleGeneralFileChange(e)}
        style={{ display: "none" }}
        id="fileInput"
      />

      {Array.isArray(formData.generalFiles) &&
        formData.generalFiles.length > 0 && (
          <UploadedFileList
            files={formData.generalFiles}
            onRemove={(file, index) => handleRemoveGeneralFile(index)}
          />
        )} */}

      {!isSubmitted && <PrimaryButton label="SAVE" onClick={handleSubmit} />}
    </form>
  );
};

export default Section9;
